import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Search, Link2, CheckCircle2, XCircle, Clock, X,
} from 'lucide-react';

import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { ListPager } from '@/components/lists/ListPager';
import { cn }            from '@/utils/cn';
import {
  integrationMappingApi,
  type MasterMappingDto, type MasterMappingCounts, type IntAccountDto, type MappingCostCenterDto,
} from '@/api/integrationMapping.api';

const STATUS_COLORS: Record<string, string> = {
  PendingMapping: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  Mapped:         'bg-teal-100 dark:bg-teal-900/30  text-teal-700 dark:text-teal-400',
  Rejected:       'bg-red-100 dark:bg-red-900/30   text-red-700 dark:text-red-400',
};

const MASTER_TYPES = [
  'Supplier', 'Customer', 'ProductCategory', 'TaxCode', 'PaymentMethod',
  'Location', 'Warehouse', 'Bank', 'CostCentre', 'StockAdjustmentReason',
];
const STATUSES = ['PendingMapping', 'Mapped', 'Rejected'];

// Required GL-account roles per master type (mirrors backend MasterTypeRoles).
const ROLES_BY_TYPE: Record<string, string[]> = {
  Supplier:              ['AccountsPayable'],
  Customer:              ['AccountsReceivable'],
  ProductCategory:       ['Inventory', 'SalesRevenue', 'COGS'],
  ProductGroup:          ['Inventory', 'SalesRevenue', 'COGS'],
  TaxCode:               ['InputTax', 'OutputTax'],
  PaymentMethod:         ['CashBankClearing'],
  StockAdjustmentReason: ['AdjustmentGain', 'AdjustmentLoss'],
};
const ROLE_LABELS: Record<string, string> = {
  AccountsPayable:    'Accounts Payable',
  AccountsReceivable: 'Accounts Receivable',
  Inventory:          'Inventory',
  SalesRevenue:       'Sales Revenue',
  COGS:               'Cost of Goods Sold',
  InputTax:           'Input Tax / VAT Receivable',
  OutputTax:          'Output Tax / VAT Payable',
  CashBankClearing:   'Cash / Bank / Clearing',
  AdjustmentGain:     'Stock Adjustment Gain',
  AdjustmentLoss:     'Stock Adjustment Loss',
  WastageExpense:     'Wastage / Damage Expense',
};
const SINGLE = '__single'; // pseudo-role for types with no defined roles
const rolesFor = (t: string): string[] => ROLES_BY_TYPE[t] ?? [];

// Master types that map to a cost centre (dimension) rather than a GL account.
const COST_CENTER_TYPES = ['Location', 'Warehouse', 'CostCentre'];
const mapsToCostCenter = (t: string): boolean => COST_CENTER_TYPES.includes(t);

const PAGE_SIZE = 25;

export const MasterMappingPage = () => {
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Permissions.INTEGRATION_MAPPING_MANAGE);

  const [items,   setItems]   = useState<MasterMappingDto[]>([]);
  const [counts,  setCounts]  = useState<MasterMappingCounts>({ total: 0, pending: 0, mapped: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [masterType, setMasterType] = useState('');
  const [status,     setStatus]     = useState('PendingMapping');
  const [search,     setSearch]     = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page,       setPage]       = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Approve modal — one account per role, or a cost centre for Location/Warehouse.
  const [approving, setApproving] = useState<MasterMappingDto | null>(null);
  const [accounts,  setAccounts]  = useState<IntAccountDto[]>([]);
  const [costCenters, setCostCenters] = useState<MappingCostCenterDto[]>([]);
  const [roleAccts, setRoleAccts] = useState<Record<string, string>>({});
  const [costCenterId, setCostCenterId] = useState('');
  const [notes,     setNotes]     = useState('');
  const [saving,    setSaving]    = useState(false);

  // Reject modal (in-app, replaces the native prompt).
  const [rejecting,   setRejecting]   = useState<MasterMappingDto | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSaving, setRejectSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, c] = await Promise.all([
        integrationMappingApi.list({
          masterType: masterType || undefined, status: status || undefined, search: search || undefined,
          pageNumber: page, pageSize: PAGE_SIZE,
        }),
        integrationMappingApi.counts(masterType || undefined),
      ]);
      // Mapping the last rows of the last page (they leave the Pending filter) empties it: step back.
      if (res.items.length === 0 && page > 1) { setPage(p => p - 1); return; }
      setItems(res.items);
      setTotalCount(res.totalCount);
      setCounts(c);
    } catch (err) {
      console.error('Failed to load master mappings', err);
    } finally {
      setLoading(false);
    }
  }, [masterType, status, search, page]);

  useEffect(() => { load(); }, [load]);

  const openApprove = async (m: MasterMappingDto) => {
    setApproving(m);
    setNotes(m.notes ?? '');

    if (mapsToCostCenter(m.masterType)) {
      setCostCenterId(m.mappedCostCenterId ?? '');
      if (costCenters.length === 0) {
        try { setCostCenters(await integrationMappingApi.costCenters()); }
        catch (err) { console.error('Failed to load cost centres', err); }
      }
      return;
    }

    const roles = rolesFor(m.masterType);
    const init: Record<string, string> = {};
    if (m.accountRolesJson) {
      try {
        for (const e of parseRoles(m.accountRolesJson)) if (e.role && e.accountId) init[e.role] = e.accountId;
      } catch { /* ignore malformed */ }
    }
    if (roles.length > 0) {
      if (m.mappedAccountId && !init[roles[0]]) init[roles[0]] = m.mappedAccountId;
    } else if (m.mappedAccountId) {
      init[SINGLE] = m.mappedAccountId;
    }
    setRoleAccts(init);

    if (accounts.length === 0) {
      try { setAccounts(await integrationMappingApi.accounts()); }
      catch (err) { console.error('Failed to load accounts', err); }
    }
  };

  const isCostCenter = approving ? mapsToCostCenter(approving.masterType) : false;
  const requiredRoles = approving && !isCostCenter ? rolesFor(approving.masterType) : [];
  const canApprove = isCostCenter
    ? !!costCenterId
    : requiredRoles.length > 0
      ? requiredRoles.every(r => roleAccts[r])
      : !!roleAccts[SINGLE];

  const confirmApprove = async () => {
    if (!approving || !canApprove) return;
    setSaving(true);
    try {
      if (isCostCenter) {
        await integrationMappingApi.approveCostCenter(approving.id, costCenterId, notes || undefined);
      } else if (requiredRoles.length > 0) {
        const payload = requiredRoles.map(r => ({ role: r, accountId: roleAccts[r] }));
        await integrationMappingApi.approveRoles(approving.id, payload, notes || undefined);
      } else {
        await integrationMappingApi.approve(approving.id, roleAccts[SINGLE], notes || undefined);
      }
      setApproving(null);
      await load();
    } catch (err) {
      console.error('Approve failed', err);
    } finally {
      setSaving(false);
    }
  };

  const openReject = (m: MasterMappingDto) => { setRejecting(m); setRejectReason(''); };

  const confirmReject = async () => {
    if (!rejecting || !rejectReason.trim()) return;
    setRejectSaving(true);
    try {
      await integrationMappingApi.reject(rejecting.id, rejectReason.trim());
      setRejecting(null);
      await load();
    } catch (err) {
      console.error('Reject failed', err);
    } finally {
      setRejectSaving(false);
    }
  };

  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const accountPicker = (value: string, onChange: (v: string) => void) => (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal">
      <option value="">Select an account…</option>
      {accounts.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name} ({a.accountType})</option>)}
    </select>
  );

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Link2 size={20} className="text-signal" />
              Master Mapping
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Link operational masters from myPOS ONE to financial accounts before posting is allowed.
            </p>
          </div>
          <button onClick={load} title="Refresh"
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors">
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Count chips */}
        <div className="flex items-center gap-2 mt-4">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
            <Clock size={12} /> {counts.pending} Pending
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400">
            <CheckCircle2 size={12} /> {counts.mapped} Mapped
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
            <XCircle size={12} /> {counts.rejected} Rejected
          </span>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
              placeholder="Search name or code…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-signal focus:border-transparent outline-none"
            />
          </div>
          <select value={masterType} onChange={e => { setMasterType(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-signal">
            <option value="">All Types</option>
            {MASTER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-signal">
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s === 'PendingMapping' ? 'Pending' : s}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Link2 size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No master mappings found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Mapped To</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(m => (
                <tr key={m.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                  <td className="px-4 py-3">
                    <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-medium text-slate-700 dark:text-slate-300">{m.masterType}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">{m.externalCode ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-900 dark:text-white">{m.externalName}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[m.mappingStatus] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                      {m.mappingStatus === 'PendingMapping' ? 'Pending' : m.mappingStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">
                    {renderMappedAccounts(m)}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{fmtDate(m.createdDate)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {canManage && m.mappingStatus !== 'Mapped' && (
                        <button onClick={() => openApprove(m)}
                          className="px-2 py-1 text-xs bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 hover:bg-teal-200 rounded transition-colors flex items-center gap-0.5">
                          <CheckCircle2 size={11} /> Map
                        </button>
                      )}
                      {canManage && m.mappingStatus === 'Mapped' && (
                        <button onClick={() => openApprove(m)}
                          className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400 hover:bg-slate-200 rounded transition-colors flex items-center gap-0.5">
                          <Link2 size={11} /> Re-map
                        </button>
                      )}
                      {canManage && m.mappingStatus !== 'Rejected' && (
                        <button onClick={() => openReject(m)} title="Reject"
                          className="p-1.5 rounded hover:bg-red-100 text-red-500 transition-colors">
                          <XCircle size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ListPager page={page} totalPages={Math.ceil(totalCount / PAGE_SIZE)} totalCount={totalCount}
        onChange={setPage} noun="mappings" />

      {/* Approve modal */}
      {approving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !saving && setApproving(null)}>
          <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white">
                {isCostCenter ? 'Map to Cost Centre' : `Map to GL Account${requiredRoles.length > 1 ? 's' : ''}`}
              </h3>
              <button onClick={() => !saving && setApproving(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="text-sm">
                <span className="text-slate-500 dark:text-gray-400">{approving.masterType}:</span>{' '}
                <span className="font-medium text-slate-900 dark:text-white">{approving.externalName}</span>
                {approving.externalCode && <span className="font-mono text-xs text-slate-400 ml-1">({approving.externalCode})</span>}
              </div>

              {isCostCenter ? (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Cost Centre</label>
                  <select value={costCenterId} onChange={e => setCostCenterId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal">
                    <option value="">Select a cost centre…</option>
                    {costCenters.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                  </select>
                  <p className="text-xs text-slate-400 mt-1.5">Postings from this location will carry this cost centre.</p>
                </div>
              ) : requiredRoles.length > 0 ? (
                requiredRoles.map(role => (
                  <div key={role}>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{ROLE_LABELS[role] ?? role}</label>
                    {accountPicker(roleAccts[role] ?? '', v => setRoleAccts(prev => ({ ...prev, [role]: v })))}
                  </div>
                ))
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">GL Account</label>
                  {accountPicker(roleAccts[SINGLE] ?? '', v => setRoleAccts(prev => ({ ...prev, [SINGLE]: v })))}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Notes (optional)</label>
                <input value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
              <button onClick={() => setApproving(null)} disabled={saving}
                className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">Cancel</button>
              <button onClick={confirmApprove} disabled={!canApprove || saving}
                className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                {saving ? <><Spinner size="sm" /> Saving…</> : <><CheckCircle2 size={14} /> Approve mapping</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !rejectSaving && setRejecting(null)}>
          <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <XCircle size={18} className="text-red-500" /> Reject mapping
              </h3>
              <button onClick={() => !rejectSaving && setRejecting(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm">
                <span className="text-slate-500 dark:text-gray-400">{rejecting.masterType}:</span>{' '}
                <span className="font-medium text-slate-900 dark:text-white">{rejecting.externalName}</span>
                {rejecting.externalCode && <span className="font-mono text-xs text-slate-400 ml-1">({rejecting.externalCode})</span>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Reason</label>
                <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} autoFocus
                  placeholder="Why is this master not mapped to the ledger?"
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal resize-none" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
              <button onClick={() => setRejecting(null)} disabled={rejectSaving}
                className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">Cancel</button>
              <button onClick={confirmReject} disabled={!rejectReason.trim() || rejectSaving}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                {rejectSaving ? <><Spinner size="sm" /> Rejecting…</> : <><XCircle size={14} /> Reject mapping</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Show the mapped target: cost centre (Location/Warehouse), the legacy single account code,
// or the per-role account codes from the JSON.
function renderMappedAccounts(m: MasterMappingDto) {
  if (m.mappingStatus === 'Rejected')
    return <span className="italic text-red-400">{m.rejectedReason}</span>;

  if (mapsToCostCenter(m.masterType))
    return m.mappedCostCenterCode ? `Cost centre: ${m.mappedCostCenterCode}` : '—';

  if (m.accountRolesJson) {
    try {
      const parsed = parseRoles(m.accountRolesJson);
      if (parsed.length > 0)
        return parsed.map(e => `${(ROLE_LABELS[e.role ?? ''] ?? e.role ?? 'Account')}: ${e.accountCode ?? '—'}`).join('  ·  ');
    } catch { /* fall through */ }
  }
  return m.mappedAccountCode ?? '—';
}

// Onboarding saves roles as { Role, AccountId, AccountCode }; this page saves { role, accountId }.
type RoleEntry = { role?: string; accountId?: string; accountCode?: string };
function parseRoles(json: string): RoleEntry[] {
  const raw = JSON.parse(json) as Record<string, string | undefined>[];
  if (!Array.isArray(raw)) return [];
  return raw.map(e => ({
    role: e?.role ?? e?.Role,
    accountId: e?.accountId ?? e?.AccountId,
    accountCode: e?.accountCode ?? e?.AccountCode,
  }));
}
